import { Controller } from "@hotwired/stimulus"
import { createConsumer, Subscription } from "@rails/actioncable"
import { CameraApiClient, CameraEventData, ApiError } from "../lib/camera_api_client"
import type { CameraFormFieldsV2, CameraSettingsV2 } from "../lib/camera_settings_types_v2"
import { settingsToFormFields, formFieldsToSettings as v2FormFieldsToSettings } from "../lib/camera_settings_types_v2"

/**
 * WebRTC Connection Controller
 *
 * Manages the dedicated WebRTC connection page with features for:
 * - Direct camera connection via WebRTC
 * - Event-driven image fetching (triggered by PIR events)
 * - Settings fetch and update (v2 cameras only)
 * - Real-time event streaming
 *
 * NOTE: WebRTC is only supported on v2 cameras (hardware_revision >= 2.0).
 * This controller exclusively handles v2 camera settings.
 *
 * @example HTML usage
 * <div data-controller="webrtc-connection"
 *      data-webrtc-connection-camera-id-value="camera-uuid">
 *   <button data-action="webrtc-connection#connect">Connect</button>
 *   <div data-webrtc-connection-target="status"></div>
 *   <img data-webrtc-connection-target="image" />
 *   <div data-webrtc-connection-target="events"></div>
 * </div>
 */
export default class extends Controller {
  static targets = [
    "status",
    "connectButton",
    "image",
    "imageContainer",
    "settingsForm",
    "settingsJson",
    "eventsList",
    "disconnectButton",
    "settingsStatus",
    "toastContainer",
    // V2 Form input targets - General
    "batteryTypeInput",
    "endpointUrlInput",
    "rtcTimeoutSecondsInput",
    "apiConnectionTimeoutSecondsInput",
    "disableSuspendInput",
    "logToSdcardInput",
    "verboseDebugInput",
    "rtcTriggerCaptureInput",
    // V2 - Operation Mode
    "operationModeNameInput",
    "captureModeInput",
    "captureCountInput",
    "captureDelayMsInput",
    "captureJpegQualityInput",
    "maxIsoDayInput",
    "maxIsoNightInput",
    "maxShutterTimeDayInput",
    "maxShutterTimeNightInput",
    "triggerBlackOutPeriodMsInput",
    "processSendmailInput",
    "emailStatusReportInput",
    // V2 - IR LED
    "dutyCycleStillCapInput",
    // V2 - PIR
    "pirSensInput",
    "pirIgnoredInput",
    "blindTimeInput",
    "pulseCounterInput",
    "windowTimeInput",
    "pirOperationModeInput",
    "filterSourceInput",
    "enablePirCronInput",
    "disablePirCronInput",
    // V2 - LTE
    "apnInput",
    "lteUsernameInput",
    "ltePasswordInput",
    "lteAntIndexInput",
    "simIndexInput",
    "rebootDelayInput",
    "wakeOnWanInput",
    "gpsEnableInput",
    // V2 - SMTP
    "smtpHostInput",
    "smtpPortInput",
    "smtpUsernameInput",
    "smtpPasswordInput",
    "smtpFromInput",
    "smtpRealNameInput",
    "smtpToAddrInput",
    "smtpToCcInput",
    "smtpToBccInput",
    // V2 - WiFi
    "staSsidInput",
    "staPasswordInput",
    "wifiAntIndexInput",
  ]
  static values = {
    cameraId: String,
  }

  declare readonly statusTarget: HTMLElement
  declare readonly connectButtonTarget: HTMLButtonElement
  declare readonly imageTarget: HTMLImageElement
  declare readonly imageContainerTarget: HTMLElement
  declare readonly settingsFormTarget: HTMLFormElement
  declare readonly settingsJsonTarget: HTMLTextAreaElement
  declare readonly eventsListTarget: HTMLElement
  declare readonly disconnectButtonTarget: HTMLButtonElement
  declare readonly settingsStatusTarget: HTMLElement
  declare readonly toastContainerTarget: HTMLElement
  // V2 General targets
  declare readonly batteryTypeInputTarget: HTMLSelectElement
  declare readonly endpointUrlInputTarget: HTMLInputElement
  declare readonly rtcTimeoutSecondsInputTarget: HTMLInputElement
  declare readonly apiConnectionTimeoutSecondsInputTarget: HTMLInputElement
  declare readonly disableSuspendInputTarget: HTMLInputElement
  declare readonly logToSdcardInputTarget: HTMLInputElement
  declare readonly verboseDebugInputTarget: HTMLInputElement
  declare readonly rtcTriggerCaptureInputTarget: HTMLInputElement
  // V2 Operation Mode targets
  declare readonly operationModeNameInputTarget: HTMLInputElement
  declare readonly captureModeInputTarget: HTMLSelectElement
  declare readonly captureCountInputTarget: HTMLInputElement
  declare readonly captureDelayMsInputTarget: HTMLInputElement
  declare readonly captureJpegQualityInputTarget: HTMLInputElement
  declare readonly maxIsoDayInputTarget: HTMLInputElement
  declare readonly maxIsoNightInputTarget: HTMLInputElement
  declare readonly maxShutterTimeDayInputTarget: HTMLInputElement
  declare readonly maxShutterTimeNightInputTarget: HTMLInputElement
  declare readonly triggerBlackOutPeriodMsInputTarget: HTMLInputElement
  declare readonly processSendmailInputTarget: HTMLInputElement
  declare readonly emailStatusReportInputTarget: HTMLInputElement
  // V2 IR LED targets
  declare readonly dutyCycleStillCapInputTarget: HTMLInputElement
  // V2 PIR targets
  declare readonly pirSensInputTarget: HTMLInputElement
  declare readonly pirIgnoredInputTarget: HTMLInputElement
  declare readonly blindTimeInputTarget: HTMLInputElement
  declare readonly pulseCounterInputTarget: HTMLInputElement
  declare readonly windowTimeInputTarget: HTMLInputElement
  declare readonly pirOperationModeInputTarget: HTMLInputElement
  declare readonly filterSourceInputTarget: HTMLInputElement
  declare readonly enablePirCronInputTarget: HTMLTextAreaElement
  declare readonly disablePirCronInputTarget: HTMLTextAreaElement
  // V2 LTE targets
  declare readonly apnInputTarget: HTMLInputElement
  declare readonly lteUsernameInputTarget: HTMLInputElement
  declare readonly ltePasswordInputTarget: HTMLInputElement
  declare readonly lteAntIndexInputTarget: HTMLSelectElement
  declare readonly simIndexInputTarget: HTMLInputElement
  declare readonly rebootDelayInputTarget: HTMLInputElement
  declare readonly wakeOnWanInputTarget: HTMLInputElement
  declare readonly gpsEnableInputTarget: HTMLInputElement
  // V2 SMTP targets
  declare readonly smtpHostInputTarget: HTMLInputElement
  declare readonly smtpPortInputTarget: HTMLInputElement
  declare readonly smtpUsernameInputTarget: HTMLInputElement
  declare readonly smtpPasswordInputTarget: HTMLInputElement
  declare readonly smtpFromInputTarget: HTMLInputElement
  declare readonly smtpRealNameInputTarget: HTMLInputElement
  declare readonly smtpToAddrInputTarget: HTMLTextAreaElement
  declare readonly smtpToCcInputTarget: HTMLTextAreaElement
  declare readonly smtpToBccInputTarget: HTMLTextAreaElement
  // V2 WiFi targets
  declare readonly staSsidInputTarget: HTMLInputElement
  declare readonly staPasswordInputTarget: HTMLInputElement
  declare readonly wifiAntIndexInputTarget: HTMLSelectElement
  // Values
  declare readonly cameraIdValue: string
  // Has targets
  declare readonly hasImageTarget: boolean
  declare readonly hasImageContainerTarget: boolean
  declare readonly hasSettingsFormTarget: boolean
  declare readonly hasSettingsJsonTarget: boolean
  declare readonly hasEventsListTarget: boolean
  declare readonly hasSettingsStatusTarget: boolean
  declare readonly hasToastContainerTarget: boolean

  // WebRTC components
  private peerConnection: RTCPeerConnection | null = null
  private dataChannel: RTCDataChannel | null = null
  private apiClient: CameraApiClient | null = null
  private signalingSubscription: Subscription | null = null
  private sessionId: string | null = null
  private iceServers: RTCIceServer[] = []

  // Connection state
  private connectionState: "disconnected" | "connecting" | "connected" | "failed" = "disconnected"
  private iceGatheringComplete = false

  // Feature state
  private eventStreamAbortController: AbortController | null = null
  private currentImageData: string | null = null

  connect() {
    console.log("[WebRTC Connection] Controller connected")
    this.updateStatus("Ready to connect")
  }

  disconnect() {
    console.log("[WebRTC Connection] Controller disconnecting")
    this.disconnectSession()
    this.cleanup()
  }

  /**
   * Connect to the camera via WebRTC
   */
  async connectToCamera(event?: Event) {
    if (event) event.preventDefault()

    if (this.connectionState === "connected" || this.connectionState === "connecting") {
      return
    }

    try {
      this.updateStatus("Creating session...")
      this.connectionState = "connecting"
      this.updateUI()

      // Step 1: Create session
      const session = await this.createSession()
      this.sessionId = session.id
      this.iceServers = session.ice_servers

      // Step 2: Subscribe to signaling channel
      await this.subscribeToSignaling()

      // Step 3: Create peer connection
      this.createPeerConnection()

      // Step 4: Create data channel
      this.createDataChannel()

      // Step 5: Create and send offer
      await this.createAndSendOffer()

      this.updateStatus("Waiting for camera to connect...")
    } catch (error) {
      console.error("[WebRTC Connection] Connection error:", error)
      this.showError(error instanceof Error ? error.message : "Connection failed")
      this.cleanup()
    }
  }

  /**
   * Disconnect from the camera
   */
  async disconnectFromCamera(event?: Event) {
    if (event) event.preventDefault()
    await this.disconnectSession()
    this.cleanup()
  }

  /**
   * Fetch current settings from camera
   */
  async fetchSettings(event?: Event) {
    if (event) event.preventDefault()

    if (!this.apiClient) {
      this.showError("Not connected to camera")
      return
    }

    try {
      this.updateStatus("Fetching settings...")
      const settings = await this.apiClient.getSettings()

      if (this.hasSettingsJsonTarget) {
        this.settingsJsonTarget.value = JSON.stringify(settings, null, 2)
      }

      this.updateStatus("Settings fetched successfully")
    } catch (error) {
      console.error("[WebRTC Connection] Error fetching settings:", error)
      this.showError(error instanceof Error ? error.message : "Failed to fetch settings")
    }
  }

  /**
   * Update settings on camera
   * This only updates the settings without triggering image capture
   */
  async updateSettings(event?: Event) {
    if (event) event.preventDefault()

    if (!this.apiClient || !this.isDataChannelOpen()) {
      this.showToast("Not connected to camera", "error")
      return
    }

    try {
      this.updateSettingsStatus("Updating...")

      // Transform form fields to v2 settings JSON
      const updatedSettings = this.formFieldsToSettings()

      // Send updated settings to camera
      await this.apiClient.updateSettings(updatedSettings)

      this.updateSettingsStatus("Updated")
      this.showToast("Settings updated successfully", "success")
    } catch (error) {
      console.error("[WebRTC Connection] Error updating settings:", error)
      this.updateSettingsStatus("Update failed")
      this.showToast(
        error instanceof Error ? error.message : "Failed to update settings",
        "error"
      )
    }
  }

  /**
   * Restore camera settings to factory defaults
   * Sends the restore-defaults command and then refreshes the form with the new settings
   */
  async restoreDefaults(event?: Event) {
    if (event) event.preventDefault()

    if (!this.apiClient || !this.isDataChannelOpen()) {
      this.showToast("Not connected to camera", "error")
      return
    }

    try {
      this.updateSettingsStatus("Restoring defaults...")

      // Send restore defaults command to camera
      await this.apiClient.restoreDefaultConfiguration()

      this.updateSettingsStatus("Fetching updated settings...")

      // Fetch the new default settings from camera
      const settings = await this.apiClient.getSettings()

      // Update the form with the new default settings
      this.populateFormFields(settings)

      this.updateSettingsStatus("Ready")
      this.showToast("Default settings restored", "success")
    } catch (error) {
      console.error("[WebRTC Connection] Error restoring defaults:", error)
      this.updateSettingsStatus("Restore failed")
      this.showToast(
        error instanceof Error ? error.message : "Failed to restore defaults",
        "error"
      )
    }
  }

  /**
   * Fetch the latest image from the camera
   * Called when a PIR trigger event is received
   */
  private async fetchLatestImage(): Promise<void> {
    if (!this.apiClient || !this.hasImageTarget) {
      return
    }

    try {
      const base64Image = await this.apiClient.getLatestImage()

      if (base64Image) {
        // Store current image data
        this.currentImageData = base64Image

        // Display the image
        this.imageTarget.src = `data:image/jpeg;base64,${base64Image}`

        if (this.hasImageContainerTarget) {
          this.imageContainerTarget.classList.remove("hidden")
        }

        console.log("[WebRTC Connection] Image updated from PIR trigger event")
      }
    } catch (error) {
      if (error instanceof ApiError && error.status === 404) {
        // No image available yet, this is normal
        console.log("[WebRTC Connection] No image available yet")
      } else {
        console.error("[WebRTC Connection] Error fetching image:", error)
      }
    }
  }

  /**
   * Wait for a new image to be captured by polling the camera
   * Compares with previously captured image and retries until different
   * @param previousImage The base64 image data to compare against
   * @param maxWaitMs Maximum time to wait in milliseconds (default: 30000)
   * @param pollIntervalMs How often to check in milliseconds (default: 1000)
   */
  private async waitForNewImage(
    previousImage: string | null,
    maxWaitMs: number = 30000,
    pollIntervalMs: number = 1000
  ): Promise<boolean> {
    if (!this.apiClient || !this.hasImageTarget) {
      return false
    }

    const startTime = Date.now()
    let attempts = 0

    while (Date.now() - startTime < maxWaitMs) {
      attempts++
      console.log(`[WebRTC Connection] Polling for new image (attempt ${attempts})...`)

      try {
        const base64Image = await this.apiClient.getLatestImage()

        // Check if this is a different image
        if (base64Image && base64Image !== previousImage) {
          // Store and display the new image
          this.currentImageData = base64Image
          this.imageTarget.src = `data:image/jpeg;base64,${base64Image}`

          if (this.hasImageContainerTarget) {
            this.imageContainerTarget.classList.remove("hidden")
          }

          console.log("[WebRTC Connection] New image captured successfully")
          return true
        }
      } catch (error) {
        if (error instanceof ApiError && error.status === 404) {
          console.log("[WebRTC Connection] No image available yet")
        } else {
          console.error("[WebRTC Connection] Error polling for image:", error)
        }
      }

      // Wait before next poll
      await new Promise(resolve => setTimeout(resolve, pollIntervalMs))
    }

    console.log("[WebRTC Connection] Timeout waiting for new image")
    return false
  }

  /**
   * Start event streaming
   */
  private async startEventStreaming(): Promise<void> {
    if (!this.apiClient || !this.hasEventsListTarget) {
      return
    }

    console.log("[WebRTC Connection] Starting event streaming")

    this.eventStreamAbortController = new AbortController()

    try {
      for await (const event of this.apiClient.subscribeToEvents()) {
        if (this.eventStreamAbortController?.signal.aborted) {
          break
        }

        this.displayEvent(event)
      }
    } catch (error) {
      if (error instanceof Error && error.message.includes("cancelled")) {
        console.log("[WebRTC Connection] Event streaming cancelled")
      } else {
        console.error("[WebRTC Connection] Event streaming error:", error)
      }
    }
  }

  /**
   * Stop event streaming
   */
  private stopEventStreaming(): void {
    if (this.eventStreamAbortController) {
      console.log("[WebRTC Connection] Stopping event streaming")
      this.eventStreamAbortController.abort()
      this.eventStreamAbortController = null
    }
  }

  /**
   * Display an event in the events list
   * Currently only pir_trigger events are emitted by the camera
   */
  private displayEvent(event: CameraEventData): void {
    if (!this.hasEventsListTarget) {
      return
    }

    // PIR trigger event means a new image was captured, fetch it
    if (event.event_type === "pir_trigger") {
      this.fetchLatestImage()
    }

    const eventElement = document.createElement("div")
    eventElement.className = "alert alert-warning py-2"
    eventElement.innerHTML = `
      <div class="flex items-start gap-2 w-full">
        <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        <div class="flex-1 min-w-0">
          <div class="font-semibold text-sm">Motion Detected</div>
          <div class="text-xs opacity-80">${new Date(event.timestamp).toLocaleString()}</div>
        </div>
      </div>
    `

    // Add to top of list
    this.eventsListTarget.insertBefore(eventElement, this.eventsListTarget.firstChild)

    // Keep only last 20 events
    while (this.eventsListTarget.children.length > 20) {
      this.eventsListTarget.removeChild(this.eventsListTarget.lastChild!)
    }
  }

  // ===== WebRTC Implementation =====

  private async createSession(): Promise<any> {
    const response = await fetch("/ajax/webrtc/sessions", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCsrfToken(),
      },
      body: JSON.stringify({
        camera_id: this.cameraIdValue,
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to create session: HTTP ${response.status}`)
    }

    return await response.json()
  }

  private async subscribeToSignaling(): Promise<void> {
    return new Promise((resolve, reject) => {
      const consumer = createConsumer()

      this.signalingSubscription = consumer.subscriptions.create(
        { channel: "WebrtcTunnel::SignalingChannel", session_id: this.sessionId },
        {
          connected: () => {
            console.log("[WebRTC Connection] Signaling channel connected")
            resolve()
          },
          disconnected: () => {
            console.log("[WebRTC Connection] Signaling channel disconnected")
          },
          rejected: () => {
            reject(new Error("Signaling channel subscription rejected"))
          },
          received: (data: any) => {
            this.handleSignalingMessage(data)
          },
        }
      )
    })
  }

  private createPeerConnection(): void {
    this.peerConnection = new RTCPeerConnection({
      iceServers: this.iceServers,
    })

    this.peerConnection.onicecandidate = (event) => {
      if (event.candidate) {
        this.sendIceCandidate(event.candidate)
      } else {
        this.iceGatheringComplete = true
        console.log("[WebRTC Connection] ICE gathering complete")
      }
    }

    this.peerConnection.onconnectionstatechange = () => {
      const state = this.peerConnection?.connectionState
      console.log("[WebRTC Connection] Peer connection state:", state)

      if (state === "connected") {
        // Don't set connectionState to "connected" yet - wait for data channel to open
        this.updateStatus("Peer connected - waiting for data channel...")
        // Note: connectionState will be set to "connected" when data channel opens
      } else if (state === "failed" || state === "closed") {
        this.connectionState = "failed"
        this.showError("Connection failed")
        this.cleanup()
      }
    }

    console.log("[WebRTC Connection] Peer connection created")
  }

  private createDataChannel(): void {
    if (!this.peerConnection) throw new Error("No peer connection")

    this.dataChannel = this.peerConnection.createDataChannel("api-tunnel", {
      ordered: true,
    })

    this.dataChannel.onopen = () => {
      console.log("[WebRTC Connection] Data channel opened")

      if (this.dataChannel) {
        this.apiClient = new CameraApiClient(this.dataChannel)
      }

      this.connectionState = "connected"
      this.updateStatus("Connected - data channel ready")
      this.updateUI()

      // Auto-fetch settings and populate form
      this.fetchAndPopulateSettings()

      // Start event streaming now that data channel is ready
      // Images will be fetched when PIR trigger events are received
      this.startEventStreaming()
    }

    this.dataChannel.onclose = () => {
      console.log("[WebRTC Connection] Data channel closed")

      if (this.apiClient) {
        this.apiClient.cleanup()
        this.apiClient = null
      }

      // Update connection state when data channel closes
      if (this.connectionState === "connected") {
        this.connectionState = "disconnected"
        this.updateStatus("Disconnected - data channel closed")
        this.updateUI()
      }
    }

    this.dataChannel.onerror = (error) => {
      console.error("[WebRTC Connection] Data channel error:", error)
      this.showError("Data channel error")

      // Update connection state on data channel error
      this.connectionState = "failed"
      this.updateUI()
    }

    console.log("[WebRTC Connection] Data channel created")
  }

  private async createAndSendOffer(): Promise<void> {
    if (!this.peerConnection) throw new Error("No peer connection")
    if (!this.sessionId) throw new Error("No session ID")

    const offer = await this.peerConnection.createOffer()
    await this.peerConnection.setLocalDescription(offer)

    const response = await fetch(`/ajax/webrtc/sessions/${this.sessionId}/offer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": this.getCsrfToken(),
      },
      body: JSON.stringify({
        type: offer.type,
        sdp: offer.sdp,
      }),
    })

    if (!response.ok) {
      throw new Error(`Failed to send offer: HTTP ${response.status}`)
    }

    console.log("[WebRTC Connection] Offer sent")
  }

  private async handleSignalingMessage(data: any): Promise<void> {
    console.log("[WebRTC Connection] Signaling message:", data.type)

    try {
      if (data.type === "answer") {
        await this.handleAnswer(data)
      } else if (data.type === "ice-candidate") {
        await this.handleRemoteIceCandidate(data)
      }
    } catch (error) {
      console.error("[WebRTC Connection] Signaling error:", error)
      this.showError(`Signaling error: ${error instanceof Error ? error.message : "Unknown"}`)
    }
  }

  private async handleAnswer(data: any): Promise<void> {
    if (!this.peerConnection) return

    const answer = new RTCSessionDescription({
      type: "answer",
      sdp: data.sdp,
    })

    await this.peerConnection.setRemoteDescription(answer)
    console.log("[WebRTC Connection] Answer set")
  }

  private async handleRemoteIceCandidate(data: any): Promise<void> {
    if (!this.peerConnection) return

    const candidate = new RTCIceCandidate({
      candidate: data.candidate,
      sdpMid: data.sdpMid,
      sdpMLineIndex: data.sdpMLineIndex,
    })

    await this.peerConnection.addIceCandidate(candidate)
    console.log("[WebRTC Connection] ICE candidate added")
  }

  private async sendIceCandidate(candidate: RTCIceCandidate): Promise<void> {
    if (!this.sessionId) return

    try {
      await fetch(`/ajax/webrtc/sessions/${this.sessionId}/ice-candidates`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        body: JSON.stringify({
          candidate: candidate.candidate,
          sdpMid: candidate.sdpMid,
          sdpMLineIndex: candidate.sdpMLineIndex,
        }),
      })
    } catch (error) {
      console.error("[WebRTC Connection] Error sending ICE candidate:", error)
    }
  }

  private async disconnectSession(): Promise<void> {
    if (!this.sessionId) return

    try {
      await fetch(`/ajax/webrtc/sessions/${this.sessionId}/close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.getCsrfToken(),
        },
        keepalive: true,
      })
    } catch (error) {
      console.error("[WebRTC Connection] Error closing session:", error)
    }
  }

  private cleanup(): void {
    console.log("[WebRTC Connection] Cleaning up")

    // Stop streaming
    this.stopEventStreaming()

    // Cleanup API client
    if (this.apiClient) {
      this.apiClient.cleanup()
      this.apiClient = null
    }

    // Close data channel
    if (this.dataChannel) {
      this.dataChannel.close()
      this.dataChannel = null
    }

    // Close peer connection
    if (this.peerConnection) {
      this.peerConnection.close()
      this.peerConnection = null
    }

    // Unsubscribe from signaling
    if (this.signalingSubscription) {
      this.signalingSubscription.unsubscribe()
      this.signalingSubscription = null
    }

    // Reset state
    this.sessionId = null
    this.iceServers = []
    this.iceGatheringComplete = false
    this.connectionState = "disconnected"
    this.updateUI()
  }

  private updateStatus(message: string): void {
    console.log("[WebRTC Connection] Status:", message)
    if (this.hasTarget("status")) {
      this.statusTarget.textContent = message
      this.statusTarget.className = "text-info"
    }
  }

  private showError(message: string): void {
    console.error("[WebRTC Connection] Error:", message)
    if (this.hasTarget("status")) {
      this.statusTarget.textContent = `Error: ${message}`
      this.statusTarget.className = "text-error"
    }
    this.connectionState = "failed"
    this.updateUI()
  }

  private updateUI(): void {
    // Update connect button
    if (this.hasTarget("connectButton")) {
      if (this.connectionState === "connected") {
        this.connectButtonTarget.classList.add("hidden")
      } else {
        this.connectButtonTarget.classList.remove("hidden")
        this.connectButtonTarget.disabled = this.connectionState === "connecting"
      }
    }

    // Update disconnect button
    if (this.hasTarget("disconnectButton")) {
      if (this.connectionState === "connected") {
        this.disconnectButtonTarget.classList.remove("hidden")
      } else {
        this.disconnectButtonTarget.classList.add("hidden")
      }
    }

    // Update settings form
    if (this.hasSettingsFormTarget) {
      if (this.connectionState === "connected") {
        this.settingsFormTarget.classList.remove("hidden")
      } else {
        this.settingsFormTarget.classList.add("hidden")
      }
    }
  }

  private getCsrfToken(): string {
    const token = document.querySelector('meta[name="csrf-token"]')?.getAttribute("content")
    return token || ""
  }

  private hasTarget(name: string): boolean {
    return this.targets.has(name)
  }

  /**
   * Check if the data channel is open and ready for communication
   */
  private isDataChannelOpen(): boolean {
    return this.dataChannel?.readyState === "open"
  }

  // ===== Settings Form Management =====

  /**
   * Fetch settings from camera and populate the form fields
   * Called automatically when data channel opens
   */
  private async fetchAndPopulateSettings(): Promise<void> {
    if (!this.apiClient) {
      console.error("[WebRTC Connection] Cannot fetch settings: no API client")
      return
    }

    try {
      this.updateSettingsStatus("Fetching settings...")
      const settings = await this.apiClient.getSettings()
      this.populateFormFields(settings)
      this.updateSettingsStatus("Ready")
      console.log("[WebRTC Connection] Settings fetched and populated")
    } catch (error) {
      console.error("[WebRTC Connection] Error fetching settings:", error)
      this.updateSettingsStatus("Fetch failed")
      this.showToast("Failed to fetch settings", "error")
    }
  }

  /**
   * Populate form fields from v2 settings JSON
   * WebRTC only works with v2 cameras
   */
  private populateFormFields(settings: CameraSettingsV2): void {
    try {
      const formFields = settingsToFormFields(settings)

      // General
      if (this.hasTarget("batteryTypeInput")) {
        this.batteryTypeInputTarget.value = String(formFields.battery_type)
      }
      if (this.hasTarget("endpointUrlInput")) {
        this.endpointUrlInputTarget.value = formFields.endpoint_url
      }
      if (this.hasTarget("rtcTimeoutSecondsInput")) {
        this.rtcTimeoutSecondsInputTarget.value = String(formFields.rtc_timeout_seconds)
      }
      if (this.hasTarget("apiConnectionTimeoutSecondsInput")) {
        this.apiConnectionTimeoutSecondsInputTarget.value = String(formFields.api_connection_timeout_seconds)
      }
      if (this.hasTarget("disableSuspendInput")) {
        (this.disableSuspendInputTarget as HTMLInputElement).checked = formFields.disable_suspend
      }
      if (this.hasTarget("logToSdcardInput")) {
        (this.logToSdcardInputTarget as HTMLInputElement).checked = formFields.log_to_sdcard
      }
      if (this.hasTarget("verboseDebugInput")) {
        (this.verboseDebugInputTarget as HTMLInputElement).checked = formFields.verbose_debug
      }
      if (this.hasTarget("rtcTriggerCaptureInput")) {
        (this.rtcTriggerCaptureInputTarget as HTMLInputElement).checked = formFields.rtc_trigger_capture
      }

      // Operation Mode
      if (this.hasTarget("operationModeNameInput")) {
        this.operationModeNameInputTarget.value = formFields.operation_mode_name
      }
      if (this.hasTarget("captureModeInput")) {
        this.captureModeInputTarget.value = String(formFields.capture_mode)
      }
      if (this.hasTarget("captureCountInput")) {
        this.captureCountInputTarget.value = String(formFields.capture_count)
      }
      if (this.hasTarget("captureDelayMsInput")) {
        this.captureDelayMsInputTarget.value = String(formFields.capture_delay_ms)
      }
      if (this.hasTarget("captureJpegQualityInput")) {
        this.captureJpegQualityInputTarget.value = String(formFields.capture_jpeg_quality)
      }
      if (this.hasTarget("maxIsoDayInput")) {
        this.maxIsoDayInputTarget.value = String(formFields.max_iso_day)
      }
      if (this.hasTarget("maxIsoNightInput")) {
        this.maxIsoNightInputTarget.value = String(formFields.max_iso_night)
      }
      if (this.hasTarget("maxShutterTimeDayInput")) {
        this.maxShutterTimeDayInputTarget.value = String(formFields.max_shutter_time_day)
      }
      if (this.hasTarget("maxShutterTimeNightInput")) {
        this.maxShutterTimeNightInputTarget.value = String(formFields.max_shutter_time_night)
      }
      if (this.hasTarget("triggerBlackOutPeriodMsInput")) {
        this.triggerBlackOutPeriodMsInputTarget.value = String(formFields.trigger_black_out_period_ms)
      }
      if (this.hasTarget("processSendmailInput")) {
        (this.processSendmailInputTarget as HTMLInputElement).checked = formFields.process_sendmail
      }
      if (this.hasTarget("emailStatusReportInput")) {
        (this.emailStatusReportInputTarget as HTMLInputElement).checked = formFields.email_status_report
      }

      // IR LED
      if (this.hasTarget("dutyCycleStillCapInput")) {
        this.dutyCycleStillCapInputTarget.value = String(formFields.duty_cycle_still_cap)
      }

      // PIR
      if (this.hasTarget("pirSensInput")) {
        this.pirSensInputTarget.value = String(formFields.pir_sens)
      }
      if (this.hasTarget("pirIgnoredInput")) {
        (this.pirIgnoredInputTarget as HTMLInputElement).checked = formFields.pir_ignored
      }
      if (this.hasTarget("blindTimeInput")) {
        this.blindTimeInputTarget.value = String(formFields.blind_time)
      }
      if (this.hasTarget("pulseCounterInput")) {
        this.pulseCounterInputTarget.value = String(formFields.pulse_counter)
      }
      if (this.hasTarget("windowTimeInput")) {
        this.windowTimeInputTarget.value = String(formFields.window_time)
      }
      if (this.hasTarget("pirOperationModeInput")) {
        this.pirOperationModeInputTarget.value = String(formFields.pir_operation_mode)
      }
      if (this.hasTarget("filterSourceInput")) {
        this.filterSourceInputTarget.value = String(formFields.filter_source)
      }
      if (this.hasTarget("enablePirCronInput")) {
        this.enablePirCronInputTarget.value = formFields.enable_pir_cron
      }
      if (this.hasTarget("disablePirCronInput")) {
        this.disablePirCronInputTarget.value = formFields.disable_pir_cron
      }

      // LTE
      if (this.hasTarget("apnInput")) {
        this.apnInputTarget.value = formFields.apn
      }
      if (this.hasTarget("lteUsernameInput")) {
        this.lteUsernameInputTarget.value = formFields.lte_username
      }
      if (this.hasTarget("ltePasswordInput")) {
        this.ltePasswordInputTarget.value = formFields.lte_password
      }
      if (this.hasTarget("lteAntIndexInput")) {
        this.lteAntIndexInputTarget.value = String(formFields.lte_ant_index)
      }
      if (this.hasTarget("simIndexInput")) {
        this.simIndexInputTarget.value = String(formFields.sim_index ?? '')
      }
      if (this.hasTarget("rebootDelayInput")) {
        this.rebootDelayInputTarget.value = String(formFields.reboot_delay)
      }
      if (this.hasTarget("wakeOnWanInput")) {
        (this.wakeOnWanInputTarget as HTMLInputElement).checked = formFields.wake_on_wan
      }
      if (this.hasTarget("gpsEnableInput")) {
        (this.gpsEnableInputTarget as HTMLInputElement).checked = formFields.gps_enable
      }

      // SMTP
      if (this.hasTarget("smtpHostInput")) {
        this.smtpHostInputTarget.value = formFields.smtp_host
      }
      if (this.hasTarget("smtpPortInput")) {
        this.smtpPortInputTarget.value = String(formFields.smtp_port)
      }
      if (this.hasTarget("smtpUsernameInput")) {
        this.smtpUsernameInputTarget.value = formFields.smtp_username
      }
      if (this.hasTarget("smtpPasswordInput")) {
        this.smtpPasswordInputTarget.value = formFields.smtp_password
      }
      if (this.hasTarget("smtpFromInput")) {
        this.smtpFromInputTarget.value = formFields.smtp_from
      }
      if (this.hasTarget("smtpRealNameInput")) {
        this.smtpRealNameInputTarget.value = formFields.smtp_real_name
      }
      if (this.hasTarget("smtpToAddrInput")) {
        this.smtpToAddrInputTarget.value = formFields.smtp_to_addr
      }
      if (this.hasTarget("smtpToCcInput")) {
        this.smtpToCcInputTarget.value = formFields.smtp_to_cc
      }
      if (this.hasTarget("smtpToBccInput")) {
        this.smtpToBccInputTarget.value = formFields.smtp_to_bcc
      }

      // WiFi
      if (this.hasTarget("staSsidInput")) {
        this.staSsidInputTarget.value = formFields.sta_ssid
      }
      if (this.hasTarget("staPasswordInput")) {
        this.staPasswordInputTarget.value = formFields.sta_password
      }
      if (this.hasTarget("wifiAntIndexInput")) {
        this.wifiAntIndexInputTarget.value = String(formFields.wifi_ant_index)
      }
    } catch (error) {
      console.error("[WebRTC Connection] Error populating form fields:", error)
    }
  }

  /**
   * Convert form field values to v2 settings JSON structure
   * WebRTC only works with v2 cameras
   */
  private formFieldsToSettings(): CameraSettingsV2 {
    const formFields: CameraFormFieldsV2 = {
      // General
      battery_type: parseInt(this.batteryTypeInputTarget.value) || 0,
      disable_suspend: (this.disableSuspendInputTarget as HTMLInputElement).checked,
      log_to_sdcard: (this.logToSdcardInputTarget as HTMLInputElement).checked,
      endpoint_url: this.endpointUrlInputTarget.value,
      rtc_timeout_seconds: parseInt(this.rtcTimeoutSecondsInputTarget.value) || 0,
      api_connection_timeout_seconds: parseInt(this.apiConnectionTimeoutSecondsInputTarget.value) || 300,
      verbose_debug: (this.verboseDebugInputTarget as HTMLInputElement).checked,
      rtc_trigger_capture: (this.rtcTriggerCaptureInputTarget as HTMLInputElement).checked,

      // Operation Mode
      operation_mode_name: this.operationModeNameInputTarget.value,
      max_iso_day: parseInt(this.maxIsoDayInputTarget.value) || 800,
      max_iso_night: parseInt(this.maxIsoNightInputTarget.value) || 1600,
      max_shutter_time_day: parseInt(this.maxShutterTimeDayInputTarget.value) || 2000,
      max_shutter_time_night: parseInt(this.maxShutterTimeNightInputTarget.value) || 1000000,
      capture_jpeg_quality: parseInt(this.captureJpegQualityInputTarget.value) || 85,
      capture_count: parseInt(this.captureCountInputTarget.value) || 1,
      capture_delay_ms: parseInt(this.captureDelayMsInputTarget.value) || 100,
      process_sendmail: (this.processSendmailInputTarget as HTMLInputElement).checked,
      capture_mode: parseInt(this.captureModeInputTarget.value) || 1,
      trigger_black_out_period_ms: parseInt(this.triggerBlackOutPeriodMsInputTarget.value) || 0,
      email_status_report: (this.emailStatusReportInputTarget as HTMLInputElement).checked,

      // IR LED
      duty_cycle_still_cap: parseInt(this.dutyCycleStillCapInputTarget.value) || 50,

      // PIR
      pir_sens: parseInt(this.pirSensInputTarget.value) || 64,
      pir_ignored: (this.pirIgnoredInputTarget as HTMLInputElement).checked,
      blind_time: parseInt(this.blindTimeInputTarget.value) || 2,
      pulse_counter: parseInt(this.pulseCounterInputTarget.value) || 1,
      window_time: parseInt(this.windowTimeInputTarget.value) || 2,
      pir_operation_mode: parseInt(this.pirOperationModeInputTarget.value) || 2,
      filter_source: parseInt(this.filterSourceInputTarget.value) || 1,
      disable_pir_cron: this.disablePirCronInputTarget.value,
      enable_pir_cron: this.enablePirCronInputTarget.value,

      // LTE
      apn: this.apnInputTarget.value,
      lte_username: this.lteUsernameInputTarget.value,
      lte_password: this.ltePasswordInputTarget.value,
      lte_ant_index: parseInt(this.lteAntIndexInputTarget.value) || 1,
      wake_on_wan: (this.wakeOnWanInputTarget as HTMLInputElement).checked,
      gps_enable: (this.gpsEnableInputTarget as HTMLInputElement).checked,
      reboot_delay: parseInt(this.rebootDelayInputTarget.value) || 60,
      sim_index: this.simIndexInputTarget.value ? parseInt(this.simIndexInputTarget.value) : null,

      // SMTP
      smtp_host: this.smtpHostInputTarget.value,
      smtp_port: parseInt(this.smtpPortInputTarget.value) || 587,
      smtp_username: this.smtpUsernameInputTarget.value,
      smtp_password: this.smtpPasswordInputTarget.value,
      smtp_from: this.smtpFromInputTarget.value,
      smtp_real_name: this.smtpRealNameInputTarget.value,
      smtp_to_addr: this.smtpToAddrInputTarget.value,
      smtp_to_cc: this.smtpToCcInputTarget.value,
      smtp_to_bcc: this.smtpToBccInputTarget.value,

      // WiFi
      sta_ssid: this.staSsidInputTarget.value,
      sta_password: this.staPasswordInputTarget.value,
      wifi_ant_index: parseInt(this.wifiAntIndexInputTarget.value) || 1,
    }

    // Use helper function to convert flat form fields to nested v2 settings
    return v2FormFieldsToSettings(formFields)
  }

  /**
   * Trigger image capture without updating settings
   * Waits for the camera to capture a new image and polls until it's ready
   */
  async triggerImageCapture(event?: Event) {
    if (event) event.preventDefault()

    if (!this.apiClient || !this.isDataChannelOpen()) {
      this.showToast("Not connected to camera", "error")
      return
    }

    try {
      // Store the current image before triggering
      const previousImage = this.currentImageData

      this.updateStatus("Triggering image capture...")

      // Send trigger command to camera
      await this.apiClient.triggerImageCapture()

      this.showToast("Image capture triggered", "info")
      this.updateStatus("Waiting for new image...")

      // Wait a short moment before starting to poll (camera needs time to process the trigger)
      await new Promise(resolve => setTimeout(resolve, 500))

      // Poll for the new image
      const success = await this.waitForNewImage(previousImage, 30000, 1000)

      if (success) {
        this.updateStatus("Connected")
        this.showToast("New image captured successfully", "success")
      } else {
        this.updateStatus("Connected")
        this.showToast("Timeout waiting for new image - camera may still be processing", "error")
      }
    } catch (error) {
      console.error("[WebRTC Connection] Error triggering image capture:", error)
      this.updateStatus("Connected")
      this.showToast(
        error instanceof Error ? error.message : "Failed to trigger image capture",
        "error"
      )
    }
  }

  /**
   * Update the settings status badge
   */
  private updateSettingsStatus(message: string): void {
    if (this.hasSettingsStatusTarget) {
      this.settingsStatusTarget.textContent = message
    }
  }

  /**
   * Show a toast notification
   */
  private showToast(message: string, type: "success" | "error" | "info" = "info"): void {
    if (!this.hasToastContainerTarget) {
      return
    }

    const alertClass = {
      success: "alert-success",
      error: "alert-error",
      info: "alert-info",
    }[type]

    const icon = {
      success: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />',
      error: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />',
      info: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>',
    }[type]

    const toast = document.createElement("div")
    toast.className = `alert ${alertClass} shadow-lg mb-2`
    toast.innerHTML = `
      <div>
        <svg xmlns="http://www.w3.org/2000/svg" class="stroke-current flex-shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
          ${icon}
        </svg>
        <span>${message}</span>
      </div>
    `

    this.toastContainerTarget.appendChild(toast)

    // Auto-remove after 3 seconds
    setTimeout(() => {
      toast.classList.add("opacity-0", "transition-opacity", "duration-300")
      setTimeout(() => {
        toast.remove()
      }, 300)
    }, 3000)
  }
}
